import type { MockMessage, MockMessageButton, MockMessageMedia, MockMessageSource } from "@/dummy";
import { AI_ASSISTANT_NAME } from "@/lib/ai-assistant";
import { hasRenderableContent } from "./message/sources/source-utils";

/**
 * A "link" button (`url` / `phone`) acts locally — it opens a link or dials,
 * never posts to the chat, and does NOT depend on the flow being at that card.
 * So it renders regardless of whether the message is the active flow step,
 * unlike `callback` buttons (which are gated on the active message).
 */
export function isLinkButton(button: Pick<MockMessageButton, "type">): boolean {
  return button.type === "url" || button.type === "phone";
}

/**
 * Map the BE's `visitorChatHistory` payload into the widget's local
 * `MockMessage` shape. Defensive on every field — history rows arrive
 * as loosely-typed JSON. Reverses the list so the oldest message is
 * first (the BE returns newest-first).
 */
export function mapHistoryToMessages(
  data: { visitorChatHistory: unknown } | null | undefined,
  locale: string,
  aiName: string = AI_ASSISTANT_NAME,
): MockMessage[] {
  if (!data) return [];
  const raw = data.visitorChatHistory;
  if (!raw || typeof raw !== "object") return [];
  const items = (raw as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  return items
    .filter((m): m is Record<string, unknown> => typeof m === "object" && m !== null)
    .map((m, idx): MockMessage => {
      const sender = (m.sender as { kind?: string; name?: string }) ?? {};
      const deliveryStatus =
        typeof m.deliveryStatus === "string"
          ? (m.deliveryStatus as MockMessage["deliveryStatus"])
          : undefined;
      // Quick-reply buttons survive across reloads — the BE persists
      // them on the message row. Previously the mapper dropped this
      // field on the floor, so re-opening the chat after a session
      // refresh showed text bubbles without the chips that were
      // there in the live session.
      // Operator avatar — flows down on the same `sender.avatar`
      // field the SSE-live path uses. Without mapping it here the
      // operator bubbles after a page reload showed the monogram
      // fallback even when the operator had a personal photo set.
      const senderAvatar =
        typeof (sender as { avatar?: unknown }).avatar === "string"
          ? (sender as { avatar: string }).avatar
          : undefined;
      const rawButtons = m.buttons;
      const buttons: MockMessageButton[] | undefined = Array.isArray(rawButtons)
        ? rawButtons
            .filter((b): b is Record<string, unknown> => typeof b === "object" && b !== null)
            .map((b) => {
              // The BE shape uses `{ label, value }`; the widget's
              // local `MockMessageButton` shape uses `{ text, payload }`.
              const label =
                typeof b.label === "string" ? b.label : typeof b.text === "string" ? b.text : "";
              const value =
                typeof b.value === "string"
                  ? b.value
                  : typeof b.payload === "string"
                    ? b.payload
                    : label;
              // Carry the cross-platform button `type` + its target field so
              // the renderer can tell a callback (resume flow) from a `url`
              // (open link) / `phone` (dial) button. Dropping these made the
              // widget treat every button as a callback — a `url` tap was
              // sent to the chat and silently ended the flow.
              const type = typeof b.type === "string" ? b.type.toLowerCase() : undefined;
              const url = typeof b.url === "string" ? b.url : undefined;
              const phoneNumber = typeof b.phoneNumber === "string" ? b.phoneNumber : undefined;
              return {
                text: label,
                payload: value,
                ...(type ? { type } : {}),
                ...(url ? { url } : {}),
                ...(phoneNumber ? { phoneNumber } : {}),
              };
            })
            .filter((b) => !!b.text)
        : undefined;
      const senderKind = (sender.kind as MockMessage["sender"]["kind"]) ?? "operator";
      // Lifecycle system notices (operator handoff). The BE marks these
      // with `sender.kind === "system"` and a `system.type`. Carry the type
      // through so the renderer draws a centered <SystemNotice> instead of a
      // bubble. History items don't carry the operator name — the live SSE
      // path does; reloaded connected rows fall back to a generic line.
      const systemPayload = ((): MockMessage["system"] => {
        if (typeof m.system !== "object" || m.system === null) return undefined;
        const s = m.system as Record<string, unknown>;
        if (s.type !== "OPERATOR_CONNECTED" && s.type !== "CHAT_RESOLVED") return undefined;
        const initiator =
          s.initiator === "VISITOR" || s.initiator === "OPERATOR" || s.initiator === "SYSTEM"
            ? s.initiator
            : undefined;
        return {
          type: s.type,
          ...(typeof s.operatorName === "string" ? { operatorName: s.operatorName } : {}),
          ...(typeof s.operatorAvatar === "string" ? { operatorAvatar: s.operatorAvatar } : {}),
          ...(initiator ? { initiator } : {}),
        };
      })();
      // Bot / AI senders read as AI_ASSISTANT_NAME in the bubble regardless of
      // what the BE labels them. Operator names flow through
      // untouched so a "Real human is now here" handoff still feels
      // human.
      const senderName = senderKind === "ai" || senderKind === "bot" ? aiName : (sender.name ?? "");
      const media = normalizeBackendMedia(m.media);
      // A media message carries its text in `caption` (the BE leaves `text`
      // null for those). History puts `caption` top-level; the live SSE shape
      // rides it under `media.caption` — handle both. Rendered AS the bubble
      // text, falling back to `text` for a normal (non-media) message.
      const caption = ((): string => {
        if (typeof m.caption === "string") return m.caption;
        const rawMedia = m.media;
        const first = Array.isArray(rawMedia) ? rawMedia[0] : rawMedia;
        if (
          first &&
          typeof first === "object" &&
          typeof (first as { caption?: unknown }).caption === "string"
        ) {
          return (first as { caption: string }).caption;
        }
        return "";
      })();
      // NOTE: the BE currently omits `sources` from the persisted history
      // payload (it's only on the live SSE event), so this is `[]` after a
      // reload until the BE serializes `sources` on history messages too.
      const sources = normalizeBackendSources(m.sources);
      const createdAt = typeof m.createdAt === "string" ? m.createdAt : undefined;
      return {
        id: String(m.id ?? `m-${idx}`),
        direction: (m.direction === "INBOUND" ? "INBOUND" : "OUTBOUND") as "INBOUND" | "OUTBOUND",
        text: caption || (typeof m.text === "string" ? m.text : ""),
        ageLabel: formatAge(createdAt, locale),
        // Keep the raw timestamp for date-separator grouping (ageLabel is
        // only the time-of-day string).
        ...(createdAt ? { createdAt } : {}),
        sender: {
          kind: senderKind,
          name: senderName,
          ...(senderAvatar ? { avatar: senderAvatar } : {}),
        },
        ...(media.length > 0 ? { media } : {}),
        ...(sources.length > 0 ? { sources } : {}),
        ...(buttons && buttons.length > 0 ? { buttons } : {}),
        ...(deliveryStatus ? { deliveryStatus } : {}),
        ...(systemPayload ? { system: systemPayload } : {}),
      };
    })
    .reverse();
}

/**
 * Normalize the BE's media shape into the widget's local
 * `MockMessageMedia` shape. Accepts either:
 *   - history rows: `media: [{ url, mimeType, filename, ... }]`
 *   - SSE media event: `media: { mediaId: { url, mimeType, filename, ... } }`
 *
 * The BE uses `mimeType` (camelCase) while the widget's renderer
 * (`MessageBubble`, `Media`, `media-lightbox`) all key off `mimetype`
 * (lowercase). Renaming is the one transform we have to do here.
 */
export function normalizeBackendMedia(raw: unknown): MockMessageMedia[] {
  if (!raw) return [];
  const items: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === "object" && raw !== null
      ? [
          // SSE single-attachment event. The CURRENT shape carries the media
          // fields on the object itself: `{ mediaId: string, url, mimeType,
          // filename, thumbnailUrl, caption }`. An older shape nested the
          // whole Media doc under `mediaId` — unwrap that ONLY when it's an
          // object (the new `mediaId` is a bare string id, not the doc).
          typeof (raw as { mediaId?: unknown }).mediaId === "object" &&
          (raw as { mediaId?: unknown }).mediaId !== null
            ? (raw as { mediaId: unknown }).mediaId
            : raw,
        ]
      : [];
  return items
    .filter((m): m is Record<string, unknown> => typeof m === "object" && m !== null)
    .map((m) => {
      const url = typeof m.url === "string" ? m.url : "";
      let mimetype =
        typeof m.mimeType === "string"
          ? m.mimeType
          : typeof m.mimetype === "string"
            ? m.mimetype
            : "";
      const alt =
        typeof m.filename === "string" ? m.filename : typeof m.alt === "string" ? m.alt : "";
      // Intrinsic dimensions of the FULL image/video — the BE Media doc carries
      // top-level width/height (Float; present for raster + video, absent for
      // files/audio) on BOTH the history payload and the SSE media event. Read
      // ONLY the top-level pair so the bubble reserves the right aspect-ratio;
      // `thumbnail.width/height` is the (possibly square-cropped) thumbnail's own
      // size and would give a wrong ratio, so it's intentionally not used.
      const width = toPositiveNumber(m.width);
      const height = toPositiveNumber(m.height);
      // A webm VOICE recording is often served as `video/webm` with NO intrinsic
      // dimensions (audio carries no width/height). The renderer treats every
      // `video/*` as a `<video controls>` element, which reserves empty
      // video-frame space for audio-only content — so relabel a dimensionless
      // `video/webm` as `audio/webm` to render the compact audio player instead.
      // A real video always carries width/height, so it stays video.
      if (mimetype === "video/webm" && !(width && height)) mimetype = "audio/webm";
      return {
        url,
        mimetype,
        alt,
        ...(width && height ? { width, height } : {}),
      };
    })
    .filter((m) => !!m.url && !!m.mimetype);
}

/** Coerce a BE numeric field (may arrive as number or numeric string) to a
 *  positive number, or `undefined` when it's missing / zero / unparseable. */
function toPositiveNumber(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Normalize the BE's AI-reply `sources` into the widget's local
 * `MockMessageSource[]`. Shared by both message paths (history rows here +
 * the SSE live append in `widget-shell`), so citations survive a reload.
 * Arrives as loosely-typed JSON — defensive on every field. Empty / content-
 * less rows (no link AND no title) are dropped so the block never renders a
 * blank card; `null` is used for absent fields (the renderer shows only what's
 * present).
 */
export function normalizeBackendSources(raw: unknown): MockMessageSource[] {
  if (!Array.isArray(raw)) return [];
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return raw
    .map((r): MockMessageSource | null => {
      if (!r || typeof r !== "object") return null;
      const o = r as Record<string, unknown>;
      return {
        link: str(o.link),
        seoTitle: str(o.seoTitle),
        seoDescription: str(o.seoDescription),
      };
    })
    .filter((s): s is MockMessageSource => !!s && hasRenderableContent(s));
}

/**
 * Decode the recorder bar's raw base64 payload back into a Blob so
 * the optimistic visitor bubble can play the audio locally — the
 * BE round-trip would otherwise leave the visitor staring at a
 * silent bubble for a beat. Match the recorder's emitted mime type
 * exactly so `<audio>` picks the right decoder.
 */
export function base64ToBlob(base64: string, mimetype: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimetype });
}

/** Format a timestamp into a short locale-aware "h:mm" age label. */
function formatAge(timestamp: string | undefined, locale: string): string {
  if (!timestamp) return "";
  try {
    return new Date(timestamp).toLocaleTimeString(locale, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
